import { StateCreator } from "zustand";
import { produce } from "immer";
import { toast } from "react-toastify";
import { useStore } from "../useStore";
import infractionService from "../../services/infractionService";
import { INFRACTION_DATE_TYPES, INFRACTION_TYPES } from "../../utils/constants/constants";
import { JourneyControlState } from "../types/journeyControl";
import journeyControlService from "../../services/journeyControlService";

const today = new Date();
today.setDate(today.getDate() - 1);
const startOfDay = new Date(today).setHours(0, 0, 0, 0);
const endOfDay = new Date(today).setHours(23, 59, 59, 999);
const fixedPeriodsForChartDays = 15;

const initialJourneyOverviewListData = {
  data: [],
  currentPage: 1,
  totalItems: 1,
  totalPages: 1,
  limitPerPage: 10,
  offset: 0,
  filters: {
    sort_key: "total",
    sort_value: "desc",
    section_id: "",
    start_date: new Date(startOfDay).toISOString(),
    end_date: new Date(endOfDay).toISOString(),
  }
};

export const createJourneyControlSlice: StateCreator<JourneyControlState> = (set, get) => ({
  journeyControl: {
    journeyOverview: {
      ...initialJourneyOverviewListData,
      setLimitPerPage: (limitPerPage: number) => {
        set(
          produce((state) => {
            state.journeyControl.journeyOverview.limitPerPage = limitPerPage;
            state.journeyControl.journeyOverview.offset = 0;
            state.journeyControl.journeyOverview.currentPage = 1;
          })
        );
        get().journeyControl.getJourneyOverview();
      },
      setCurrentPage: (page: number) => {
        set(
          produce((state) => {
            state.journeyControl.journeyOverview.currentPage = page;
            state.journeyControl.journeyOverview.offset = page - 1;
          })
        );
        get().journeyControl.getJourneyOverview();
      },
      setFilters: (filters) => {
        set(
          produce((state) => {
            state.journeyControl.journeyOverview.filters = filters;
            state.journeyControl.journeyOverview.offset = 0;
            state.journeyControl.journeyOverview.currentPage = 1;
          })
        );
        useStore.getState().modal.clearModals();
        get().journeyControl.getJourneyOverview();
        get().journeyControl.getAdhesionChartData();
      },
    },
    selectedDateType: INFRACTION_DATE_TYPES[0].value,
    isLoadingJourneyOverview:false,
    isLoadingAdhesionChartData:false,
    getJourneyOverview: () => {
      let section;

      set(
        produce((state) => {
          state.journeyControl.isLoadingjourneyOverview = true;
          section = state.admin.data.section?._id;
        })
      );

      const filters = { ...get().journeyControl.journeyOverview.filters };
      const limit = get().journeyControl.journeyOverview.limitPerPage;
      const offset = get().journeyControl.journeyOverview.offset;
      const selectedDateType = get().journeyControl.selectedDateType

      journeyControlService
        .getJourneyOverview({ filters, limit, offset }, section)
        .then(({ data, totalItems }: any) => {
          set(
            produce((state) => {
              state.journeyControl.journeyOverview.data = data;
              state.journeyControl.journeyOverview.totalItems = totalItems;
              state.journeyControl.journeyOverview.totalPages = Math.ceil(
                totalItems / state.journeyControl.journeyOverview.limitPerPage
              );
            })
          );
        })
        .catch((error) => {
          toast.error(error.message);
        })
        .finally(() => {
          set(
            produce((state) => {
              state.journeyControl.isLoadingjourneyOverview = false;
            })
          );
        });
    },
    getAdhesionChartData: (userId?: string) => {
      set(
        produce((state) => {
          if(userId) state.journeyControl.isLoadingInfractionsTypePerPeriodUserChartData = true;
          else state.journeyControl.isLoadingInfractionsTypePerPeriodChartData = true;
        })
      );
      const selectedDateType = get().journeyControl.selectedDateType
      const referenceDay = selectedDateType === INFRACTION_DATE_TYPES[0].value ? new Date(get().journeyControl.journeyOverview.filters.start_date) : null;
      const referenceMonth = selectedDateType === INFRACTION_DATE_TYPES[1].value ? new Date() : null;
      const granularity = selectedDateType === INFRACTION_DATE_TYPES[0].value ? "DAY" : "MONTH";

      let startDate: any = new Date()
      let endDate: any = new Date();
      if(referenceDay) {
        endDate = new Date(referenceDay.setHours(23, 59, 59, 999));
        endDate = new Date(endDate).toISOString();
        const day = new Date(referenceDay).getDate();
        const month = new Date(referenceDay).getMonth();
        const year = new Date(referenceDay).getFullYear();
        startDate = new Date(year, month, day - fixedPeriodsForChartDays).setHours(0, 0, 0, 0);
        startDate = new Date(startDate).toISOString();
      }

      infractionService
        .getInfractionTypePerPeriodChartData(startDate, endDate, granularity, referenceMonth?.toISOString(), userId)
        .then((data: any[]) => {
          set(
            produce((state) => {
              data = data.map((entry) => {
                return {
                  ...entry,
                  'EXTRA_HOUR': undefined
                }
              });
              
              if(userId) state.journeyControl.infractionsTypePerPeriodUserChartData = data;
              else state.journeyControl.infractionsTypePerPeriodChartData = data;
              const infractionSummarized: any = [];

              if(data.length > 2) {
                infractionSummarized.push(Object.entries(data[data.length - 1]).map(([key, value]) => {
                  return {
                    type: key,
                    total: value,
                  };
                }));
  
                infractionSummarized.push(Object.entries(data[data.length - 2]).map(([key, value]) => {
                  return {
                    type: key,
                    total: value,
                  };
                }));
              }
              if(!userId) state.journeyControl.infractionsSummarized = infractionSummarized;

              const convertedData: any = {};
              data.forEach((entry) => {
                Object.keys(entry).forEach((key) => {
                  if (key !== "date") {
                    if (!convertedData[key]) {
                      convertedData[key] = [];
                    }
            
                    convertedData[key].push({
                      date: entry.date,
                      value: entry[key] ?? 0,
                      volume: entry[key] ?? 0,
                    });
                  }
                });
              });
              const allDates = data.map((entry) => entry.date);
              Object.keys(convertedData).forEach((key) => {
                const existingDates = convertedData[key].map((item: any) => item.date);
                allDates.forEach((date) => {
                  if (!existingDates.includes(date)) {
                    convertedData[key].push({
                      date: date,
                      value: 0,
                      volume: 0,
                    });
                  }
                });
                convertedData[key].sort((a: any, b: any) => new Date(a.date).getTime() - new Date(b.date).getTime());
              });
              if(!userId) state.journeyControl.infractionsTypeSparklinesChartData = convertedData;
            })
          );
        })
        .catch((error) => {
          toast.error(error.message);
        })
        .finally(() => {
          set(
            produce((state) => {
              if(userId) state.journeyControl.isLoadingInfractionsTypePerPeriodUserChartData = false;
              else state.journeyControl.isLoadingInfractionsTypePerPeriodChartData = false;
            })
          );
        });
    }
  }
});

